/**
 * SpeciesCatalogTable Component
 *
 * Table view for species catalog with sortable columns
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-299
 */

import { Edit, Trash, History, Fish, ChevronUp, ChevronDown } from 'lucide-react';
import { useState } from 'react';

/**
 * Get badge color for species type
 */
function getTypeBadgeClass(speciesType) {
  const classes = {
    fish: 'bg-blue-100 text-blue-800',
    game: 'bg-green-100 text-green-800',
    wildlife: 'bg-purple-100 text-purple-800'
  };
  return classes[speciesType] || 'bg-gray-100 text-gray-800';
}

/**
 * Sort icon component
 */
function SortIcon({ field, sortField, sortOrder }) {
  if (field !== sortField) {
    return (
      <div className="inline-flex flex-col ml-1 opacity-30">
        <ChevronUp className="w-3 h-3 -mb-1" />
        <ChevronDown className="w-3 h-3" />
      </div>
    );
  }

  return sortOrder === 'asc' ? (
    <ChevronUp className="w-4 h-4 ml-1 inline" />
  ) : (
    <ChevronDown className="w-4 h-4 ml-1 inline" />
  );
}

export default function SpeciesCatalogTable({
  species,
  sortField,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
  onToggleActive,
  onViewAudit
}) {
  const [hoveredRow, setHoveredRow] = useState(null);

  /**
   * Sortable column header
   */
  const SortableHeader = ({ field, label }) => (
    <th
      onClick={() => onSort(field)}
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
    >
      <div className="flex items-center">
        {label}
        <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Image
            </th>
            <SortableHeader field="common_name" label="Common Name" />
            <SortableHeader field="scientific_name" label="Scientific Name" />
            <SortableHeader field="species_type" label="Type" />
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {species.map((s) => (
            <tr
              key={s.id}
              onMouseEnter={() => setHoveredRow(s.id)}
              onMouseLeave={() => setHoveredRow(null)}
              className={`transition-colors ${
                hoveredRow === s.id ? 'bg-gray-50' : ''
              }`}
            >
              {/* Image */}
              <td className="px-6 py-4 whitespace-nowrap">
                {s.thumbnail_url || s.image_url ? (
                  <img
                    src={s.thumbnail_url || s.image_url}
                    alt={s.common_name}
                    className="w-16 h-16 object-cover rounded border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                    <Fish className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </td>

              {/* Common Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {s.common_name}
                </div>
                {s.search_keywords && s.search_keywords.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    aka: {s.search_keywords.slice(0, 2).join(', ')}
                    {s.search_keywords.length > 2 && '...'}
                  </div>
                )}
              </td>

              {/* Scientific Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-600 italic">
                  {s.scientific_name || '—'}
                </div>
              </td>

              {/* Species Type */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeBadgeClass(
                    s.species_type
                  )}`}
                >
                  {s.species_type}
                </span>
              </td>

              {/* Category */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{s.category || '—'}</div>
              </td>

              {/* Status Toggle */}
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  onClick={() => onToggleActive(s.id, s.is_active)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    s.is_active ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={s.is_active}
                  title={s.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                >
                  <span className="sr-only">
                    {s.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      s.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </td>

              {/* Actions */}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  {/* Edit Button */}
                  <button
                    onClick={() => onEdit(s)}
                    className="text-blue-600 hover:text-blue-900 transition-colors"
                    title="Edit species"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="sr-only">Edit</span>
                  </button>

                  {/* Audit Button */}
                  <button
                    onClick={() => onViewAudit(s)}
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                    title="View audit history"
                  >
                    <History className="w-4 h-4" />
                    <span className="sr-only">View audit history</span>
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => onDelete(s)}
                    className="text-red-600 hover:text-red-900 transition-colors"
                    title="Deactivate species"
                  >
                    <Trash className="w-4 h-4" />
                    <span className="sr-only">Deactivate</span>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
